import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Flex, Progress, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, DownOutlined, UndoOutlined } from '@ant-design/icons';
import type { DeckDecision } from '../services/deckDecisions.js';

const { Text } = Typography;

const THRESHOLD = 110;

export interface SwipeSummary {
  learned: number;
  known: number;
  /** "Not now": the words that come back at the end of the deck. */
  later: number;
}

interface Props<T, R> {
  title: string;
  items: T[];
  /** What the card shows for one item. */
  renderFace: (item: T) => React.ReactNode;
  /** Colour of the card's border (a word's gender colour, for example). */
  borderColor?: (item: T) => string;
  /** Resolves once the choice is durably applied (Learn just enqueues, so this returns fast). */
  onDecision: (item: T, decision: DeckDecision) => Promise<R>;
  onUndo: (item: T, decision: DeckDecision, result: R) => Promise<void>;
  onDone: (summary: SwipeSummary) => void;
  /** Shows a button to leave before the last item (the first-run flow has 100 words). */
  onStop?: () => void;
  stopLabel?: string;
}

interface LastDecision<T, R> {
  item: T;
  decision: DeckDecision;
  result: R;
}

const delta = (decision: DeckDecision, by: 1 | -1) => ({
  learned: decision === 'learn' ? by : 0,
  known: decision === 'known' ? by : 0,
  later: decision === 'notnow' ? by : 0,
});

/**
 * One-card-at-a-time triage, by swipe, buttons or keys: right (→) = Learn it, left (←) = I know
 * it, down (↓) = Not now, Backspace = undo the last choice. Generic over what the cards are.
 */
export function DeckSwipe<T, R>({
  title,
  items,
  renderFace,
  borderColor,
  onDecision,
  onUndo,
  onDone,
  onStop,
  stopLabel = 'Stop here',
}: Props<T, R>): React.ReactElement | null {
  const [index, setIndex] = useState(0);
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [exiting, setExiting] = useState<DeckDecision | null>(null);
  const [last, setLast] = useState<LastDecision<T, R> | null>(null);
  const [counts, setCounts] = useState<SwipeSummary>({ learned: 0, known: 0, later: 0 });
  const start = useRef<{ x: number; y: number } | null>(null);
  const busy = useRef(false);
  const doneCalled = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const item = items[index];

  const commit = useCallback(
    async (decision: DeckDecision) => {
      if (item === undefined || busy.current) return;
      busy.current = true;
      setExiting(decision);
      await new Promise((r) => setTimeout(r, 180));
      try {
        const result = await onDecision(item, decision);
        setLast({ item, decision, result });
        const d = delta(decision, 1);
        setCounts((c) => ({
          learned: c.learned + d.learned,
          known: c.known + d.known,
          later: c.later + d.later,
        }));
        setIndex((i) => i + 1);
      } finally {
        setExiting(null);
        setDrag({ x: 0, y: 0 });
        busy.current = false;
      }
    },
    [item, onDecision]
  );

  const undo = useCallback(async () => {
    if (!last || busy.current) return;
    busy.current = true;
    try {
      await onUndo(last.item, last.decision, last.result);
      const d = delta(last.decision, -1);
      setCounts((c) => ({
        learned: c.learned + d.learned,
        known: c.known + d.known,
        later: c.later + d.later,
      }));
      setIndex((i) => Math.max(0, i - 1));
      setLast(null);
    } finally {
      busy.current = false;
    }
  }, [last, onUndo]);

  useEffect(() => {
    if (index >= items.length && !doneCalled.current) {
      doneCalled.current = true;
      onDone(counts);
    }
  }, [index, items.length, counts, onDone]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // The Decks tab stays mounted while another tab is showing, and people type in text fields:
      // the keys only apply while this card is on screen and no field has focus.
      if (!rootRef.current?.isConnected) return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const target = e.target;
      if (
        target instanceof Element &&
        target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }
      if (e.key === 'ArrowRight') void commit('learn');
      else if (e.key === 'ArrowLeft') void commit('known');
      else if (e.key === 'ArrowDown') void commit('notnow');
      else if (e.key === 'Backspace') void undo();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [commit, undo]);

  if (item === undefined) return null;

  const clamp = (v: number) => Math.max(0, Math.min(1, v));
  const stampLearn = exiting === 'learn' ? 1 : clamp(drag.x / THRESHOLD);
  const stampKnown = exiting === 'known' ? 1 : clamp(-drag.x / THRESHOLD);
  const stampLater = exiting === 'notnow' ? 1 : clamp(drag.y / THRESHOLD);

  let transform = `translate(${drag.x}px, ${drag.y}px) rotate(${drag.x / 15}deg)`;
  if (exiting === 'learn') transform = 'translate(700px, 0) rotate(20deg)';
  if (exiting === 'known') transform = 'translate(-700px, 0) rotate(-20deg)';
  if (exiting === 'notnow') transform = 'translate(0, 700px)';

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    start.current = { x: e.clientX, y: e.clientY };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!start.current) return;
    setDrag({ x: e.clientX - start.current.x, y: e.clientY - start.current.y });
  };
  const onPointerUp = () => {
    if (!start.current) return;
    start.current = null;
    setDragging(false);
    if (drag.y > THRESHOLD && drag.y > Math.abs(drag.x)) void commit('notnow');
    else if (drag.x > THRESHOLD) void commit('learn');
    else if (drag.x < -THRESHOLD) void commit('known');
    else setDrag({ x: 0, y: 0 });
  };

  const stamp = (
    label: string,
    color: string,
    opacity: number,
    side: 'left' | 'right' | 'center'
  ) => (
    <div
      style={{
        position: 'absolute',
        top: 16,
        ...(side === 'left'
          ? { left: 16 }
          : side === 'right'
            ? { right: 16 }
            : { left: '50%', transform: 'translateX(-50%)' }),
        padding: '2px 12px',
        border: `3px solid ${color}`,
        color,
        borderRadius: 8,
        fontWeight: 700,
        fontSize: 16,
        letterSpacing: 1,
        opacity,
        rotate: `${side === 'left' ? -12 : side === 'right' ? 12 : 0}deg`,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </div>
  );

  return (
    <div ref={rootRef} style={{ maxWidth: 420, margin: '0 auto', userSelect: 'none' }}>
      <Flex justify="space-between" style={{ marginBottom: 6 }}>
        <Text type="secondary">{title}</Text>
        <Text type="secondary">
          {Math.min(index + 1, items.length)} / {items.length}
        </Text>
      </Flex>
      <Progress
        percent={Math.round((index / items.length) * 100)}
        showInfo={false}
        strokeColor="#7c3aed"
        size={['100%', 4]}
        style={{ marginBottom: 14 }}
      />

      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="group"
        aria-label={`Word ${Math.min(index + 1, items.length)} of ${items.length}`}
        data-testid="swipe-card"
        style={{
          position: 'relative',
          touchAction: 'none',
          cursor: dragging ? 'grabbing' : 'grab',
          transform,
          transition: dragging ? 'none' : 'transform 0.2s ease',
          background: '#fff',
          border: `2px solid ${borderColor?.(item) ?? '#e5e7eb'}`,
          borderRadius: 16,
          padding: '48px 18px 24px',
          minHeight: 400,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        }}
      >
        {stamp('LEARN IT', '#7c3aed', stampLearn, 'right')}
        {stamp('I KNOW IT', '#b45309', stampKnown, 'left')}
        {stamp('NOT NOW', '#475569', stampLater, 'center')}
        {renderFace(item)}
      </div>

      <Flex justify="center" align="center" gap={18} style={{ marginTop: 20 }}>
        <Flex vertical align="center" gap={2}>
          <Button
            shape="circle"
            size="large"
            icon={<CloseOutlined />}
            onClick={() => void commit('known')}
            style={{ width: 52, height: 52, borderColor: '#b45309', color: '#b45309' }}
            aria-label="I know it"
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            I know it
          </Text>
        </Flex>
        <Flex vertical align="center" gap={2}>
          <Button
            shape="circle"
            icon={<DownOutlined />}
            onClick={() => void commit('notnow')}
            aria-label="Not now"
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Not now
          </Text>
        </Flex>
        <Flex vertical align="center" gap={2}>
          <Button
            shape="circle"
            size="large"
            icon={<CheckOutlined />}
            onClick={() => void commit('learn')}
            style={{ width: 52, height: 52, borderColor: '#6d28d9', color: '#6d28d9' }}
            aria-label="Learn it"
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Learn it
          </Text>
        </Flex>
      </Flex>

      <Flex vertical align="center" gap={4} style={{ marginTop: 10 }}>
        <Button
          type="text"
          size="small"
          icon={<UndoOutlined />}
          disabled={!last}
          onClick={() => void undo()}
        >
          Undo last
        </Button>
        <Text style={{ fontSize: 12, color: '#475569' }}>
          Keys: ← I know it · ↓ not now · → learn it · Backspace undo
        </Text>
      </Flex>

      {onStop && (
        <Flex vertical align="center" gap={4} style={{ marginTop: 20 }}>
          <Button onClick={onStop}>{stopLabel}</Button>
          <Text style={{ fontSize: 12, color: '#475569' }}>
            The rest of the deck waits for you in the Decks tab.
          </Text>
        </Flex>
      )}
    </div>
  );
}
